import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { billingService } from '../services/BillingService'
import { useStripePerformanceMonitor } from '../hooks/usePerformanceMonitor'

interface StripeConfig {
  publishableKey: string
  [key: string]: any
}

interface StripeContextType {
  stripe: Stripe | null
  stripeConfig: StripeConfig | null
  loading: boolean
  error: string | null
  initializeStripe: () => Promise<void>
  isInitialized: boolean
}

const StripeContext = createContext<StripeContextType | undefined>(undefined)

interface StripeProviderProps {
  children: React.ReactNode
  /** When set (e.g. from billing-comprehensive), skips GET /api/stripe/config before loadStripe. */
  prefetchedStripeConfig?: StripeConfig | null
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children, prefetchedStripeConfig = null }) => {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const initializationRef = useRef<Promise<void> | null>(null)

  // Monitor performance for Stripe operations (only in development)
  const { metrics, reportViolation } = useStripePerformanceMonitor()
  const reportViolationRef = useRef(reportViolation)

  // Update ref when reportViolation changes
  useEffect(() => {
    reportViolationRef.current = reportViolation
  }, [reportViolation])

  const initializeStripe = useCallback(async () => {
    // Prevent multiple simultaneous initializations
    if (initializationRef.current) {
      return initializationRef.current
    }

    if (isInitialized && stripe) {
      return
    }

    initializationRef.current = (async () => {
      try {
        setLoading(true)
        setError(null)

        // Check if we already have a cached config
        const cachedConfig = localStorage.getItem('stripe-config')
        const cachedTimestamp = localStorage.getItem('stripe-config-timestamp')
        const now = Date.now()
        const cacheExpiry = 5 * 60 * 1000 // 5 minutes

        let config: StripeConfig | null = null

        // Use cached config if it's still valid
        if (cachedConfig && cachedTimestamp && (now - parseInt(cachedTimestamp)) < cacheExpiry) {
          try {
            config = JSON.parse(cachedConfig)
            if (import.meta.env.DEV) {
              console.log('Using cached Stripe config')
            }
          } catch (e) {
            console.warn('Failed to parse cached Stripe config, fetching fresh')
          }
        }

        if (!config && prefetchedStripeConfig?.publishableKey) {
          config = prefetchedStripeConfig
          localStorage.setItem('stripe-config', JSON.stringify(config))
          localStorage.setItem('stripe-config-timestamp', now.toString())
        }

        // Fetch fresh config if no valid cache.
        // getStripeConfig() has a built-in 10 s AbortController timeout.
        if (!config) {
          if (import.meta.env.DEV) {
            console.log('Fetching fresh Stripe config')
          }
          const result = await billingService.getStripeConfig()

          if (result.success && result.data) {
            const directConfig = result.data.direct
            const cloudConfig = result.data.cloud?.config
            config = directConfig || cloudConfig

            if (config && config.publishableKey) {
              // Cache the config
              localStorage.setItem('stripe-config', JSON.stringify(config))
              localStorage.setItem('stripe-config-timestamp', now.toString())
            }
          }
        }

        if (!config || !config.publishableKey) {
          throw new Error('Stripe publishable key not found')
        }

        setStripeConfig(config)

        // Load Stripe with performance optimizations.
        // Race against a 12 s timeout so a slow/blocked Stripe CDN never hangs
        // the component indefinitely on bad connections.
        const startTime = performance.now()
        const stripeTimeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('loadStripe timed out after 12s')), 12000)
        )

        let stripeInstance
        try {
          stripeInstance = await Promise.race([loadStripe(config.publishableKey), stripeTimeout])
        } catch (stripeError: any) {
          // If there's a version compatibility issue, retry with empty options
          if (stripeError.message?.includes('apiVersion') || stripeError.message?.includes('Unsupported')) {
            console.warn('Stripe version compatibility issue, retrying with minimal config:', stripeError.message)
            stripeInstance = await Promise.race([loadStripe(config.publishableKey, {}), stripeTimeout])
          } else {
            throw stripeError
          }
        }

        const loadTime = performance.now() - startTime

        // Report if Stripe loading took too long
        if (loadTime > 1000) { // 1 second threshold
          reportViolationRef.current(loadTime)
        }

        if (!stripeInstance) {
          throw new Error('Failed to load Stripe')
        }

        setStripe(stripeInstance)
        setIsInitialized(true)
        if (import.meta.env.DEV) {
          console.log('Stripe initialized successfully')
        }

      } catch (err: any) {
        console.error('Error initializing Stripe:', err)
        setError(err.message || 'Failed to initialize Stripe')
        // Clear invalid cache
        localStorage.removeItem('stripe-config')
        localStorage.removeItem('stripe-config-timestamp')
      } finally {
        setLoading(false)
        initializationRef.current = null
      }
    })()

    return initializationRef.current
  }, [isInitialized, stripe, prefetchedStripeConfig])

  // Initialize Stripe when the provider mounts
  useEffect(() => {
    initializeStripe()
  }, [initializeStripe])

  // Cleanup function
  useEffect(() => {
    return () => {
      // Clear any pending initialization
      initializationRef.current = null
    }
  }, [])

  const value = useMemo<StripeContextType>(() => ({
    stripe,
    stripeConfig,
    loading,
    error,
    initializeStripe,
    isInitialized,
  }), [stripe, stripeConfig, loading, error, initializeStripe, isInitialized])

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  )
}

export const useStripeContext = (): StripeContextType => {
  const context = useContext(StripeContext)
  if (context === undefined) {
    throw new Error('useStripeContext must be used within a StripeProvider')
  }
  return context
}

// Hook for debounced operations
export const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
